import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import GppGoodRoundedIcon from '@mui/icons-material/GppGoodRounded';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import { useApprovePromotion, useCampaigns, usePromotions, usePromotionsForIds, useRejectPromotion } from '../../hooks';
import { buildPromotionSummary } from '../../lib/promoSummary';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import type { ToastSeverity } from '../../lib/useToast';
import type { PromotionResponse, PromotionSummary } from '../../model/types';
import { PromotionReviewDrawer } from './PromotionReviewDrawer';

export interface ApprovalQueuePanelProps {
  onResult: (severity: ToastSeverity, message: string) => void;
}

/** "Approvals" — the approval queue (plan-011 §8.7); mirrors `PriceApprovalsPanel`'s skeleton. */
export function ApprovalQueuePanel({ onResult }: ApprovalQueuePanelProps) {
  const { t } = useTranslation('promotions');
  const { canApprove } = usePromoPermissions();

  const [rejectTarget, setRejectTarget] = useState<PromotionSummary | null>(null);
  const [reason, setReason] = useState('');
  const [reviewTarget, setReviewTarget] = useState<PromotionSummary | null>(null);

  const queue = usePromotions({ status: 'PENDING_APPROVAL', size: 200 });
  const campaigns = useCampaigns({ size: 200 });
  const items = queue.data?.content ?? [];
  const detailQueries = usePromotionsForIds(items.map((p) => p.id));
  const detailById = useMemo(() => {
    const map = new Map<string, PromotionResponse>();
    detailQueries.forEach((q) => {
      if (q.data) map.set(q.data.id, q.data);
    });
    return map;
  }, [detailQueries]);

  const approveMutation = useApprovePromotion();
  const rejectMutation = useRejectPromotion();

  const campaignName = (campaignId: string | null | undefined) =>
    campaigns.data?.content.find((c) => c.id === campaignId)?.name;

  const reviewDetail = reviewTarget ? (detailById.get(reviewTarget.id) ?? null) : null;

  if (queue.isLoading) return <LoadingState label={t('approvals.loading')} />;
  if (queue.isError) {
    return (
      <ErrorState
        title={t('approvals.loadError.title')}
        message={queue.error?.message ?? t('common:error.unknown')}
        onRetry={() => void queue.refetch()}
      />
    );
  }
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<GppGoodRoundedIcon sx={{ fontSize: 48 }} />}
        title={t('approvals.empty.title')}
        description={t('approvals.empty.description')}
      />
    );
  }

  return (
    <>
      <TablePageShell>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('approvals.table.promotion')}</TableCell>
                <TableCell>{t('approvals.table.campaign')}</TableCell>
                <TableCell>{t('approvals.table.discount')}</TableCell>
                <TableCell>{t('approvals.table.reason')}</TableCell>
                <TableCell>{t('approvals.table.author')}</TableCell>
                <TableCell align="right">{t('approvals.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const detail = detailById.get(item.id);
                return (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2">{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {item.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{campaignName(item.campaignId) ?? t('promotionsList.noCampaign')}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>
                      {detail ? buildPromotionSummary(t, detail) : '…'}
                    </TableCell>
                    <TableCell>{detail?.approvalReason ?? '—'}</TableCell>
                    <TableCell>{detail?.createdBy ?? '—'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title={!canApprove ? t('approvals.approvePermissionHint') : ''}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<CheckCircleRoundedIcon />}
                              disabled={!canApprove || approveMutation.isPending}
                              onClick={() =>
                                approveMutation.mutate(item.id, {
                                  onSuccess: () => onResult('success', t('approvals.approved')),
                                  onError: (e) => onResult('error', e.message),
                                })
                              }
                            >
                              {t('approvals.approve')}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={!canApprove ? t('approvals.approvePermissionHint') : ''}>
                          <span>
                            <Button
                              size="small"
                              variant="text"
                              color="error"
                              disabled={!canApprove}
                              onClick={() => {
                                setRejectTarget(item);
                                setReason('');
                              }}
                            >
                              {t('approvals.reject')}
                            </Button>
                          </span>
                        </Tooltip>
                        <Button size="small" onClick={() => setReviewTarget(item)}>
                          {t('approvals.review')}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TablePageShell>

      <Dialog open={rejectTarget !== null} onClose={() => setRejectTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('approvals.rejectDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('approvals.rejectDialog.reasonLabel')}
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)} disabled={rejectMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={rejectMutation.isPending || !reason.trim()}
            onClick={() => {
              if (!rejectTarget) return;
              rejectMutation.mutate(
                { promotionId: rejectTarget.id, reason: reason.trim() },
                {
                  onSuccess: () => {
                    setRejectTarget(null);
                    onResult('success', t('approvals.rejected'));
                  },
                  onError: (e) => onResult('error', e.message),
                },
              );
            }}
          >
            {t('approvals.reject')}
          </Button>
        </DialogActions>
      </Dialog>

      <PromotionReviewDrawer
        open={reviewTarget !== null}
        promotion={reviewDetail}
        canApprove={canApprove}
        approving={approveMutation.isPending}
        onClose={() => setReviewTarget(null)}
        onApprove={() => {
          if (!reviewTarget) return;
          approveMutation.mutate(reviewTarget.id, {
            onSuccess: () => {
              onResult('success', t('approvals.approved'));
              setReviewTarget(null);
            },
            onError: (e) => onResult('error', e.message),
          });
        }}
        onReject={() => {
          if (!reviewTarget) return;
          setRejectTarget(reviewTarget);
          setReason('');
          setReviewTarget(null);
        }}
      />
    </>
  );
}
